// Libraries
import { Box, Card, Center, Flex, Skeleton, Text } from '@chakra-ui/react';
// Components
import { ChartLegend, CustomLegendStyle, Spinner } from '@atoms/index';
import { ChartRatingValues, LineTooltipRating } from '@molecules/index';
import { LineChart } from '@organisms/index';
// Utils
import { formatDateToUtc, formatTickToDate } from '@helpers/index';
import text from '@translations/en.json';
import { RatingList, graphCIIDataToVictoryData } from '@utils/index';
// Types
import { SimulationLineChartCardProps } from './SimulationLineChartCard.types';

const legendItems = [
  {
    styleKey: CustomLegendStyle.blackDotted,
    title: text.cii_simulator.chart_simulation_card.required,
  },
  {
    styleKey: CustomLegendStyle.blackBase,
    title: text.cii_simulator.chart_simulation_card.actual,
  },
  {
    styleKey: CustomLegendStyle.scatter,
    title: text.cii_simulator.chart_simulation_card.current,
  },
  {
    styleKey: CustomLegendStyle.blackBaseDashed,
    title: text.cii_simulator.chart_simulation_card.simulated_range,
  },
];

/**
 * `SimulationLineChartCard` component renders a card containing a line chart for a given simulation data.
 * It displays the current and forecasted ratings at the top, the graph in the middle,
 * and the legend at the bottom. The chart shows a series of data points over time.
 *
 * @component
 * @param {SimulationLineChartCardProps} props - The props to configure the SimulationLineChartCard component.
 * @param {boolean} props.isLoading - Indicates if the component is in a loading state or if data is available.
 * @param {GraphData} props.graphData - The data used to render the chart, including the current and forecasted ratings.
 *
 * @returns {ReactElement} The rendered SimulationLineChartCard component.
 */
export const SimulationLineChartCard = ({ isLoading, graphData }: SimulationLineChartCardProps) => {
  const {
    graphicsData,
    ciiRatingBoundaries,
    operationalYear,
    requiredCiiScore,
    currentRating,
    forecastRating,
    lastNoonReportDate
  } = graphData ?? {};
  
  const {
    victoryAreas,
    victoryLines,
    victoryScatter,
    domain,
    tickValues,
    xLimit,
    tooltipBlacklist,
    error,
    xTickValues,
  } = graphCIIDataToVictoryData(
    graphicsData,
    ciiRatingBoundaries,
    operationalYear,
    requiredCiiScore
  );

  const showGraph = victoryLines && tickValues && !error && !isLoading;
  const lastNoonReportLabel = lastNoonReportDate ? formatDateToUtc(lastNoonReportDate) : text.no_data_available;
  
  return (
    <Card w='full' justify='center' align='center' gap={4} px={{ base: 3, md: 6 }} py={{ base: 3, md: 5 }}>
      <Flex
        w='full'
        gap={{ base: 2, md: 3 }}
        justifyContent='space-between'
        direction={{ base: 'column', md: 'row' }}
        alignItems={{ base: 'start', md: 'center' }}
      >
        <Box>
          <Text fontSize='l' fontWeight='medium'>
            {text.cii_simulator.chart_simulation_card.title}
          </Text>
          <Flex gap={1}>
            <Text color='grey.100' fontSize='s'>
              {text.cii_simulator.chart_simulation_card.last_noon_report}
            </Text>
            <Skeleton isLoaded={!isLoading}>
              <Text color='grey.100' fontSize='s'>
                {lastNoonReportLabel}
              </Text>
            </Skeleton>
          </Flex>
        </Box>
        <ChartRatingValues isLoading={isLoading} current={currentRating} forecasted={forecastRating} />
      </Flex>
      <Center w='full' flex={1} minH={320}>
        {isLoading ? (
          <Spinner size='md' />
        ) : (
          <Flex w='full' direction='column' gap={2} align='center'>
            <Flex w='full' h='full' flexDir='column' alignItems='center'>
              {showGraph ? (
                <LineChart
                  victoryLines={victoryLines}
                  victoryAreas={victoryAreas}
                  victoryPointScatters={victoryScatter}
                  ratingRanges={ciiRatingBoundaries}
                  padding={{ top: 8, bottom: 22, left: 30, right: 20 }}
                  additionalAxis={{
                    dependentAxis: true,
                    tickValues,
                    tickFormat: RatingList,
                    orientation: 'right',
                  }}
                  tickFormatters={{
                    xTickFormatter: formatTickToDate('MMM'),
                  }}
                  showGreyAxis={false}
                  tooltip={<LineTooltipRating ratingRanges={ciiRatingBoundaries} />}
                  tooltipBlacklist={tooltipBlacklist}
                  invertAxes={{
                    invertYAxis: true,
                  }}
                  xLimit={xLimit}
                  tickValues={{ xTickValues }}
                  domain={domain}
                />
              ) : (
                // TODO: Implement a real error element to display in case something went wrong creating the table
                <Text>{error}</Text>
              )}
            </Flex>
          </Flex>
        )}
      </Center>
      <ChartLegend items={legendItems} />
    </Card>
  );
};
